import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import SEO from "../components/SEO";
import Modal from "react-modal";
import { firestore } from "../firebase";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";

/* ---------- Layout ---------- */

const Page = styled.main`
  min-height: 70vh;
  padding: 40px 18px;
  background: #f7fafc;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Hero = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 22px;
  margin-bottom: 18px;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(26px, 3vw, 44px);
  color: #111827;
  font-weight: 900;
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  color: #4b5563;
  line-height: 1.65;
  max-width: 820px;
`;

/* ---------- Tabs ---------- */

const Tabs = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

const Tab = styled.button`
  appearance: none;
  border: 1px solid #e5e7eb;
  background: ${({ $active }) => ($active ? "#0f766e" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#111827")};
  font-weight: 900;
  border-radius: 999px;
  padding: 10px 16px;
  cursor: pointer;
  transition: transform 160ms ease, filter 160ms ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.02);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

/* ---------- Card ---------- */

const Card = styled.section`
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 18px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 10px;
  font-size: 18px;
  font-weight: 950;
  color: #111827;
`;

const HtmlContent = styled.div`
  color: #374151;
  line-height: 1.75;
  font-size: 15px;

  /* gör inmatad HTML lite snyggare */
  p {
    margin: 10px 0;
  }

  a {
    color: #0f766e;
    font-weight: 900;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  ul,
  ol {
    margin: 10px 0 10px 18px;
  }
`;

const Actions = styled.div`
  margin-top: 14px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const PrimaryLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(15, 118, 110, 0.25);
  background: rgba(15, 118, 110, 0.1);
  color: #0f766e;
  font-weight: 950;
  text-decoration: none;

  &:hover {
    filter: brightness(1.04);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

/* ---------- Intyg grid ---------- */

const Grid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  margin-top: 12px;
`;

const ThumbBtn = styled.button`
  border: 1px solid #e5e7eb;
  background: #fff;
  padding: 0;
  border-radius: 16px;
  overflow: hidden;
  cursor: zoom-in;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.06);
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid rgba(15, 118, 110, 0.45);
    outline-offset: 3px;
  }
`;

/* ✅ Thumbnail som visar HELA intyget (contain) */
const ThumbFrame = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  background: #f3f4f6; /* “papper”-känsla */
  padding: 10px;
`;

const Thumb = styled.img`
  width: 70%;
  height: 70%;
  object-fit: contain; /* ✅ visar hela intyget */
  display: block;
  background: #fff;
  border-radius: 10px;
`;

const Empty = styled.div`
  padding: 12px 0;
  color: #6b7280;
  font-weight: 800;
`;

/* ---------- Modal ---------- */

Modal.setAppElement("#root");

/* ✅ FIX: centrerad på desktop + över header + responsivt */
const modalStyles = {
  overlay: {
    backgroundColor: "rgba(0,0,0,0.82)",
    zIndex: 9999, // ✅ över header
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px",
  },
  content: {
    position: "relative",
    inset: "auto",
    transform: "none", // ✅ vi centrerar med flex istället
    padding: 0,
    borderRadius: "18px",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.18)",
    background: "#0b1220",
    width: "min(1100px, 94vw)",
    maxHeight: "92vh",
    boxShadow: "0 18px 70px rgba(0,0,0,0.55)",
  },
};

const ModalTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(11, 18, 32, 0.92);
  color: #fff;
`;

const ModalTitle = styled.div`
  font-weight: 950;
  font-size: 14px;
  opacity: 0.95;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CloseBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border-radius: 12px;
  padding: 8px 10px;
  cursor: pointer;
  font-weight: 950;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.55);
    outline-offset: 3px;
  }
`;

const ModalBody = styled.div`
  background: #000;
  display: grid;
  place-items: center;
  padding: 12px;
`;

/* ✅ Bilden anpassar sig till både mobil + desktop */
const ModalImg = styled.img`
  max-width: 100%;
  max-height: calc(92vh - 56px); /* 56px ≈ ModalTop höjd */
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  background: #fff;
  border-radius: 12px;
`;

/* ---------- Component ---------- */

const Content = () => {
  const [activeTab, setActiveTab] = useState("föreningen");
  const [foreningText, setForeningText] = useState("");
  const [styrelseText, setStyrelseText] = useState("");
  const [intygList, setIntygList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalSrc, setModalSrc] = useState("");
  const [modalLabel, setModalLabel] = useState("Intyg");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Text
        const docRef = doc(firestore, "föreningstext", "content");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setForeningText(docSnap.data()?.föreningText || "");
          setStyrelseText(docSnap.data()?.styrelseText || "");
        }

        // Intyg
        const snap = await getDocs(collection(firestore, "intyg"));
        const list = snap.docs.map((d) => ({
          id: d.id,
          fileUrl: d.data()?.fileUrl,
          title: d.data()?.title || `Intyg ${d.id}`,
        }));
        setIntygList(list);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const openModal = (src, label) => {
    if (!src) return;
    setModalSrc(src);
    setModalLabel(label || "Intyg");
    setModalOpen(true);
  };

  const headerText = useMemo(() => {
    if (activeTab === "intyg") return "Intyg & erkännanden";
    return "Föreningen";
  }, [activeTab]);

  return (
    <>
      <SEO
        title="Föreningen"
        description="Information om föreningen, styrelsen och intyg."
        keywords="Föreningen, styrelsen, intyg, Hofors BGK"
      />

      <Page>
        <Container>
          <Hero>
            <div>
              <Title>{headerText}</Title>
              <Subtitle>
                Här hittar du information om föreningen och styrelsen – samt intyg/erkännanden.
              </Subtitle>
            </div>
          </Hero>

          <Tabs>
            <Tab
              type="button"
              $active={activeTab === "föreningen"}
              onClick={() => setActiveTab("föreningen")}
            >
              Föreningen
            </Tab>
            <Tab type="button" $active={activeTab === "intyg"} onClick={() => setActiveTab("intyg")}>
              Intyg
            </Tab>
          </Tabs>

          <Card>
            {activeTab === "föreningen" && (
              <>
                <SectionTitle>Föreningen</SectionTitle>

                {loading ? (
                  <Empty>Laddar föreningens information…</Empty>
                ) : (
                  <HtmlContent
                    dangerouslySetInnerHTML={{
                      __html: foreningText || "<p>Ingen information tillgänglig.</p>",
                    }}
                  />
                )}

                <Actions>
                  <PrimaryLink
                    href="https://hcponline.se/MemberList.aspx?tabpage=ClubDetails&HCPCLUB_ID=360"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Besök vår HCP Online-sida
                  </PrimaryLink>
                </Actions>

                <div style={{ height: 16 }} />

                <SectionTitle>Styrelsen</SectionTitle>

                {loading ? (
                  <Empty>Laddar styrelsens information…</Empty>
                ) : (
                  <HtmlContent
                    dangerouslySetInnerHTML={{
                      __html: styrelseText || "<p>Ingen styrelseinformation tillgänglig.</p>",
                    }}
                  />
                )}
              </>
            )}

            {activeTab === "intyg" && (
              <>
                <SectionTitle>Intyg</SectionTitle>

                {loading ? (
                  <Empty>Laddar intyg…</Empty>
                ) : intygList.length ? (
                  <Grid>
                    {intygList.map((i) => (
                      <ThumbBtn
                        key={i.id}
                        type="button"
                        onClick={() => openModal(i.fileUrl, i.title)}
                        aria-label={`Öppna ${i.title}`}
                      >
                        <ThumbFrame>
                          <Thumb src={i.fileUrl} alt={i.title} />
                        </ThumbFrame>
                      </ThumbBtn>
                    ))}
                  </Grid>
                ) : (
                  <Empty>Inga intyg hittades.</Empty>
                )}
              </>
            )}
          </Card>
        </Container>
      </Page>

      <Modal
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        style={modalStyles}
        contentLabel={modalLabel}
        bodyOpenClassName="ReactModal__Body--open"
      >
        <ModalTop>
          <ModalTitle>{modalLabel}</ModalTitle>
          <CloseBtn type="button" onClick={() => setModalOpen(false)}>
            Stäng ✕
          </CloseBtn>
        </ModalTop>

        <ModalBody>{modalSrc ? <ModalImg src={modalSrc} alt={modalLabel} /> : null}</ModalBody>
      </Modal>
    </>
  );
};

export default Content;





